import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import PublicLayout from "@/components/PublicLayout";
import Seo from "@/components/Seo";
import { api, formatError } from "@/lib/api";
import { SUBURBS } from "@/lib/constants";
import { Check, Loader2, Star, MapPin, ArrowRight } from "lucide-react";

const AUD = (n) => {
  const num = Number(n);
  const hasCents = Math.round(num * 100) % 100 !== 0;
  return `A$${num.toFixed(hasCents ? 2 : 0)}`;
};

export default function Pricing() {
  const [pricing, setPricing] = useState(null);
  const [busy, setBusy] = useState("");
  const [contractorEmail, setContractorEmail] = useState("");
  const [featuredSuburb, setFeaturedSuburb] = useState("donnybrook");

  useEffect(() => {
    api.get("/payments/pricing").then((r) => setPricing(r.data)).catch(() => {});
  }, []);

  const checkout = async (purpose, context = {}) => {
    if (!contractorEmail) {
      toast.error("Enter your business email first — that's your account key.");
      return;
    }
    setBusy(purpose);
    try {
      const { data } = await api.post("/payments/checkout", {
        purpose,
        origin_url: window.location.origin,
        context: { contractor_email: contractorEmail, ...context },
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatError(err));
      setBusy("");
    }
  };

  const tiers = [
    {
      key: "free",
      name: "Free",
      price: "A$0",
      cadence: "forever",
      features: [
        "Submit contractor application",
        "Receive occasional matched leads",
        "Standard email visibility",
        "No paid features",
      ],
      cta: null,
      accent: false,
    },
    {
      key: "subscription_pro",
      name: "Pro",
      price: pricing ? AUD(pricing.subscription_pro.amount) : "A$249",
      cadence: "per 30 days · incl. GST",
      features: [
        "Priority lead matching in your suburbs",
        "Unlimited lead contact reveals within your plan",
        "Faster notification on new enquiries",
        "Basic performance dashboard",
      ],
      cta: "Start Pro",
      accent: true,
    },
    {
      key: "subscription_prem",
      name: "Premium",
      price: pricing ? AUD(pricing.subscription_prem.amount) : "A$499",
      cadence: "per 30 days · incl. GST",
      features: [
        "Everything in Pro",
        "First-look access on new leads",
        "Included in the featured contractor rotation",
        "Priority support & onboarding",
      ],
      cta: "Start Premium",
      accent: false,
    },
  ];

  return (
    <PublicLayout>
      <Seo title="Contractor Pricing | Site Cleanup" description="Contractor plans, per-lead unlocks and featured suburb pricing for the Site Cleanup network." />
      <section className="bg-asphalt text-white py-20">
        <div className="rs-container">
          <span className="rs-eyebrow text-safety">For contractors</span>
          <h1 className="mt-3 text-5xl md:text-7xl uppercase max-w-3xl leading-[0.95]">Pricing that scales with your workload.</h1>
          <p className="mt-4 max-w-2xl text-white/75">Choose how you want to receive site clean-up leads: pay per lead, subscribe monthly, or lock in a featured position in a suburb you dominate.</p>
        </div>
      </section>

      <section className="py-16">
        <div className="rs-container">
          <label className="block max-w-md mb-10">
            <span className="rs-label">Your business email (used to activate your plan)</span>
            <input data-testid="pricing-email" type="email" className="rs-input" placeholder="you@yourbusiness.com.au" value={contractorEmail} onChange={(e) => setContractorEmail(e.target.value)} />
          </label>

          <h2 className="font-display text-3xl uppercase mb-6">Subscription plans</h2>
          <div className="grid md:grid-cols-3 gap-4">
            {tiers.map((t) => (
              <div key={t.key} className={`p-6 border ${t.accent ? "border-safety bg-white ring-4 ring-safety/10" : "border-border bg-white"} flex flex-col`} data-testid={`tier-${t.key}`}>
                <div className="flex items-center gap-2">
                  <h3 className="font-display uppercase text-2xl">{t.name}</h3>
                  {t.accent && <span className="text-[10px] font-bold bg-safety text-white px-2 py-0.5 uppercase tracking-widest">Popular</span>}
                </div>
                <p className="mt-3 text-4xl font-display">{t.price}<span className="text-sm text-muted-foreground font-sans font-normal ml-1">{t.cadence}</span></p>
                <ul className="mt-6 space-y-2 text-sm flex-1">
                  {t.features.map((f) => (
                    <li key={f} className="flex gap-2 items-start"><Check size={16} className="text-safety mt-0.5 shrink-0"/> <span>{f}</span></li>
                  ))}
                </ul>
                {t.cta ? (
                  <button
                    disabled={busy === t.key}
                    onClick={() => checkout(t.key)}
                    data-testid={`checkout-${t.key}`}
                    className={`mt-6 ${t.accent ? "rs-btn-primary" : "rs-btn-outline"} disabled:opacity-70`}
                  >
                    {busy === t.key ? <><Loader2 className="animate-spin mr-2" size={16}/> Redirecting…</> : t.cta}
                  </button>
                ) : (
                  <Link to="/partner" className="rs-btn-outline mt-6">Apply free</Link>
                )}
              </div>
            ))}
          </div>

          <div className="mt-16 grid md:grid-cols-2 gap-6">
            {/* Per-lead unlock */}
            <div className="border border-border p-6 bg-white">
              <div className="flex items-center gap-3">
                <span className="w-10 h-10 grid place-items-center bg-safety/10 text-safety"><Star size={18}/></span>
                <h3 className="font-display uppercase text-2xl">Per-lead unlock</h3>
              </div>
              <p className="mt-3 text-4xl font-display">{pricing ? AUD(pricing.lead_unlock.amount) : "A$49.99"}<span className="text-sm text-muted-foreground font-sans font-normal ml-1">per lead · incl. GST</span></p>
              <p className="mt-3 text-sm text-muted-foreground">Not ready to subscribe? Unlock individual leads on demand — pay when we send you a match and only if the job looks like a fit.</p>
              <p className="mt-4 text-xs text-muted-foreground">Unlock links are sent to your inbox when a matched lead lands. You only pay when you decide to reveal contact details.</p>
            </div>

            {/* Featured suburb */}
            <div className="border border-border p-6 bg-white">
              <div className="flex items-center gap-3">
                <span className="w-10 h-10 grid place-items-center bg-safety/10 text-safety"><MapPin size={18}/></span>
                <h3 className="font-display uppercase text-2xl">Featured suburb</h3>
              </div>
              <p className="mt-3 text-4xl font-display">{pricing ? AUD(pricing.featured_suburb.amount) : "A$299"}<span className="text-sm text-muted-foreground font-sans font-normal ml-1">per suburb / 30 days · incl. GST</span></p>
              <p className="mt-3 text-sm text-muted-foreground">Be the featured contractor for a specific suburb. New leads for that suburb are offered to you first.</p>
              <label className="block mt-4">
                <span className="rs-label">Choose your suburb</span>
                <select data-testid="featured-suburb-select" className="rs-input" value={featuredSuburb} onChange={(e) => setFeaturedSuburb(e.target.value)}>
                  {SUBURBS.filter((s) => !["melbourne-metro","regional-victoria"].includes(s.slug)).map((s) => (
                    <option key={s.slug} value={s.slug}>{s.name}</option>
                  ))}
                </select>
              </label>
              <button
                disabled={busy === "featured_suburb"}
                onClick={() => checkout("featured_suburb", { suburb: featuredSuburb })}
                data-testid="checkout-featured-suburb"
                className="rs-btn-primary w-full mt-4 disabled:opacity-70"
              >
                {busy === "featured_suburb" ? <><Loader2 className="animate-spin mr-2" size={16}/> Redirecting…</> : "Feature my business"}
              </button>
            </div>
          </div>

          <p className="mt-12 text-xs text-muted-foreground max-w-3xl">
            All prices in Australian Dollars and <strong className="text-asphalt">include 10% GST</strong>. Paid plans and featured listings are billed as 30-day access periods; auto-renewal is off by default and can be arranged on request. Subscribing does not guarantee a specific lead volume — actual leads depend on customer enquiries in your service areas.
            <br/><br/>
            <strong className="text-asphalt">Builder-referred jobs:</strong> Site Cleanup takes a <strong>10% commission (excluding GST)</strong> on the invoiced value of any clean-up work delivered through a builder-partnership referral. Commission is calculated on the ex-GST portion of the contractor's invoice and reconciled monthly.
            <br/><br/>
            See our <Link to="/terms" className="underline">Terms & Partnership Conditions</Link> for full details.
          </p>
        </div>
      </section>

      <section className="py-16 bg-safety text-white">
        <div className="rs-container flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="font-display uppercase text-3xl leading-tight">Not a contractor?</h3>
            <p className="text-white/90 mt-1">Homeowners and builders — head to the free quote form.</p>
          </div>
          <Link to="/request-quote" className="inline-flex items-center gap-2 bg-asphalt text-white px-8 py-4 font-semibold uppercase tracking-widest border-2 border-asphalt hover:bg-white hover:text-asphalt transition-colors">Get a free quote <ArrowRight size={16}/></Link>
        </div>
      </section>
    </PublicLayout>
  );
}
